import React, { useState, useCallback, useEffect } from 'react';
import { motion, PanInfo, useMotionValue, useTransform } from 'framer-motion';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Heart, X, MapPin, Bed, Bath, Car, ChevronLeft, ChevronRight, ChevronUp, Calendar, Square, Info } from 'lucide-react';
import { Property } from '../../types';
import { cn } from '../../lib/utils';
import PropertyInfoPanel from './PropertyInfoPanel';
interface EnhancedSwipeCardProps {
  property: Property;
  matchScore: number;
  onSwipe?: (direction: 'left' | 'right') => void;
  className?: string;
  isNext?: boolean;
}
const EnhancedSwipeCard: React.FC<EnhancedSwipeCardProps> = ({
  property,
  matchScore,
  onSwipe,
  className,
  isNext = false
}) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [showInfoPanel, setShowInfoPanel] = useState(false);

  // Motion values for drag with enhanced Tinder-style physics
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const rotate = useTransform(x, [-300, 0, 300], [-15, 0, 15]);
  const opacity = useTransform(x, [-300, -150, 0, 150, 300], [0.6, 1, 1, 1, 0.6]);

  // Tinder-style overlay opacity - starts showing at ~25px, full at ~120px
  const likeOpacity = useTransform(x, [0, 40, 150], [0, 0.5, 1]);
  const passOpacity = useTransform(x, [-150, -40, 0], [1, 0.5, 0]);
  const images = property.images || [];
  const currentImage = images[currentImageIndex] || '/placeholder.svg';

  // Reset image index when property changes
  useEffect(() => {
    setCurrentImageIndex(0);
  }, [property.id]);
  const handleDragEnd = useCallback((event: any, info: PanInfo) => {
    setIsDragging(false);
    const {
      offset,
      velocity
    } = info;
    
    // Elite swipe thresholds: 100px or fast velocity (>500)
    const swipeThreshold = 100;
    const velocityThreshold = 500;
    const swipe = Math.abs(offset.x) > swipeThreshold || Math.abs(velocity.x) > velocityThreshold;
    
    if (swipe) {
      const direction = offset.x > 0 ? 'right' : 'left';
      
      // Haptic feedback for swipes
      if ('vibrate' in navigator) {
        navigator.vibrate(direction === 'right' ? [10, 50, 10] : [20]);
      }
      
      onSwipe?.(direction);
    } else {
      // Smooth spring-back with tuned physics
      x.set(0);
      y.set(0);
    }
  }, [onSwipe, x, y]);
  const handleButtonSwipe = useCallback((direction: 'left' | 'right') => {
    // Animate card with smooth fly-out
    const targetX = direction === 'right' ? 400 : -400;
    x.set(targetX);

    // Trigger swipe after animation
    setTimeout(() => {
      onSwipe?.(direction);
    }, 250);
  }, [onSwipe, x]);
  const handleImageNavigation = useCallback((direction: 'prev' | 'next', e: React.MouseEvent) => {
    e.stopPropagation();
    if (images.length <= 1) return;
    
    // Light haptic feedback
    if ('vibrate' in navigator) {
      navigator.vibrate(10);
    }
    
    setCurrentImageIndex(prev => direction === 'next' ? (prev + 1) % images.length : (prev - 1 + images.length) % images.length);
  }, [images.length]);
  const handleImageClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (images.length <= 1) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const clickZone = rect.width * 0.3; // 30% zones on each side

    if (clickX < clickZone) {
      // Left click - previous image
      setCurrentImageIndex(prev => (prev - 1 + images.length) % images.length);
    } else if (clickX > rect.width - clickZone) {
      // Right click - next image
      setCurrentImageIndex(prev => (prev + 1) % images.length);
    }
  }, [images.length]);
  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    }).format(price);
  };
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  };
  return <>
      <motion.div className={cn("relative w-full h-full cursor-grab active:cursor-grabbing", className)} style={{
      x,
      y,
      rotate,
      opacity
    }} drag={!isNext ? "x" : false} dragConstraints={{
      left: 0,
      right: 0,
      top: 0,
      bottom: 0
    }} dragElastic={0.7} onDragStart={() => setIsDragging(true)} onDragEnd={handleDragEnd} whileTap={{
      cursor: "grabbing"
    }} animate={{
      scale: isNext ? 0.95 : 1
    }} transition={{
      scale: {
        type: "spring",
        stiffness: 300,
        damping: 30
      }
    }}>
        {/* Tinder-style LIKE overlay */}
        <motion.div className="pointer-events-none absolute inset-0 rounded-2xl z-10 flex items-start justify-start p-8" style={{
        opacity: likeOpacity
      }}>
          <div className="border-4 border-green-500 rounded-xl px-6 py-3 rotate-[-15deg]">
            <p className="text-5xl font-black text-green-500">LIKE</p>
          </div>
        </motion.div>

        {/* Tinder-style NOPE overlay */}
        <motion.div className="pointer-events-none absolute inset-0 rounded-2xl z-10 flex items-start justify-end p-8" style={{
        opacity: passOpacity
      }}>
          <div className="border-4 border-red-500 rounded-xl px-6 py-3 rotate-[15deg]">
            <p className="text-5xl font-black text-red-500">NOPE</p>
          </div>
        </motion.div>

        {/* Card Content - Premium glass morphism with gradient border */}
        <Card className="w-full h-full bg-gradient-to-br from-slate-900/40 via-slate-800/30 to-slate-900/40 backdrop-blur-xl border-2 border-white/90 overflow-hidden shadow-[0_20px_80px_-15px_rgba(0,0,0,0.5)] ring-1 ring-white/20 rounded-3xl flex flex-col">
          {/* Full-height image with bottom overlay info */}
          <div className="relative w-full h-full bg-slate-950">
            {images && images.length > 0 ? <>
                <img src={currentImage} alt={property.title} className="w-full h-full object-cover" loading="lazy" onClick={handleImageClick} />
                
                {/* Strong gradient for text legibility */}
                <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />

                {/* Match Score Badge - Top Right */}
                <div className="absolute top-6 right-6 z-20">
                  <motion.div initial={{
                scale: 0
              }} animate={{
                scale: 1
              }} transition={{
                delay: 0.2,
                type: "spring"
              }}>
                    <Badge className="bg-black text-white px-2 py-1 text-sm font-bold shadow-xl border-2 border-white/80 backdrop-blur-sm">
                      {matchScore}% Match
                    </Badge>
                  </motion.div>
                </div>

                {/* Desktop: Image navigation arrows */}
                {images.length > 1 && <>
                    <button onClick={e => handleImageNavigation('prev', e)} className="hidden md:flex absolute left-4 top-1/2 -translate-y-1/2 z-10 items-center justify-center w-12 h-12 rounded-full bg-white/90 shadow-xl transition-all active:scale-95" aria-label="Previous image">
                      <ChevronLeft className="w-6 h-6 text-slate-900" />
                    </button>
                    <button onClick={e => handleImageNavigation('next', e)} className="hidden md:flex absolute right-4 top-1/2 -translate-y-1/2 z-10 items-center justify-center w-12 h-12 rounded-full bg-white/90 shadow-xl transition-all active:scale-95" aria-label="Next image">
                      <ChevronRight className="w-6 h-6 text-slate-900" />
                    </button>
                  </>}

                {/* Image indicators */}
                {images.length > 1 && <div className="absolute top-4 left-0 right-0 z-10 flex gap-1.5 px-4">
                    {images.map((_, idx) => <div key={idx} className={cn("h-1.5 flex-1 rounded-full transition-all", idx === currentImageIndex ? "bg-white shadow-lg" : "bg-white/30")} />)}
                  </div>}

                {/* Bottom overlay with property info - Simplified */}
                <div className="absolute bottom-0 left-0 right-0 z-10 p-6 space-y-3">
                  {/* Title - Full Width */}
                  <h3 className="text-2xl md:text-3xl font-bold text-white drop-shadow-2xl line-clamp-2 w-full">
                    {property.title}
                  </h3>
                  
                  {/* Location - Immediately Below Title */}
                  <p className="text-white/90 flex items-center gap-2 drop-shadow-lg text-base">
                    <MapPin className="w-4 h-4" />
                    {property.city}, {property.state}
                  </p>

                  {/* Price and Basic Details in One Row */}
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-4 text-white drop-shadow-lg">
                      <div className="flex items-center gap-1.5">
                        <Bed className="w-5 h-5" />
                        <span className="font-semibold">{property.bedrooms}</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <Bath className="w-5 h-5" />
                        <span className="font-semibold">{property.bathrooms}</span>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl md:text-3xl font-bold text-white drop-shadow-2xl">
                        {formatPrice(property.rent)}
                      </p>
                      <p className="text-xs text-white/80 drop-shadow-lg">/month</p>
                    </div>
                  </div>
                </div>
              </> : <div className="w-full h-full flex items-center justify-center">
                <p className="text-slate-400">No images available</p>
              </div>}
          </div>

        </Card>
      </motion.div>

      {/* Action Buttons - Below Card - Only show for current card */}
      {!isNext && <div className="flex justify-center items-center gap-6 mt-6 mb-24 pointer-events-auto py-0 relative z-10">
          <motion.button 
            whileTap={{ scale: 0.9 }} 
            onClick={() => handleButtonSwipe('left')} 
            className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-white flex items-center justify-center transition-all border-2 border-red-200 shadow-lg" 
            aria-label="Pass"
          >
            <X className="w-8 h-8 md:w-10 md:h-10 text-red-500" />
          </motion.button>

          <motion.button 
            whileTap={{ scale: 0.9 }} 
            onClick={e => {
              e.stopPropagation();
              setShowInfoPanel(true);
            }} 
            className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center transition-all border-2 border-white shadow-lg" 
            aria-label="More Info"
          >
            <ChevronUp className="w-8 h-8 md:w-10 md:h-10 text-white" />
          </motion.button>

          <motion.button 
            whileTap={{ scale: 0.9 }} 
            onClick={() => handleButtonSwipe('right')} 
            className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-white flex items-center justify-center transition-all border-2 border-green-200 shadow-lg" 
            aria-label="Like"
          >
            <Heart className="w-8 h-8 md:w-10 md:h-10 text-green-500" />
          </motion.button>
        </div>}

      {/* Property Info Panel */}
      <PropertyInfoPanel property={property} isOpen={showInfoPanel} onClose={() => setShowInfoPanel(false)} matchScore={matchScore} />
    </>;
};
export default EnhancedSwipeCard;